import {useCallback, useEffect, useState} from 'react'

// Per-venue cart, persisted in localStorage. Prices/names are a display cache —
// the server recomputes everything at checkout (WBA-4513).
export type StoreCartLine = {
  stockItemId: string
  variantId: string | null
  name: string
  variantLabel: string | null
  price: number
  thumbUrl: string | null
  quantity: number
}

const storageKey = (venueId: string) => `store-cart:${venueId}`

const readCart = (venueId?: string): StoreCartLine[] => {
  if (!venueId) {
    return []
  }
  try {
    const raw = localStorage.getItem(storageKey(venueId))
    const parsed = raw ? (JSON.parse(raw) as StoreCartLine[]) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const lineKey = (line: {stockItemId: string; variantId: string | null}) =>
  `${line.stockItemId}::${line.variantId || ''}`

// Every hook instance owns its own state, so without a broadcast the header's
// count goes stale the moment another component adds a line.
const CART_CHANGED_EVENT = 'store-cart-changed'

export const useStoreCart = (venueId?: string) => {
  const [lines, setLines] = useState<StoreCartLine[]>(() => readCart(venueId))
  useEffect(() => {
    setLines(readCart(venueId))
    const sync = () => setLines(readCart(venueId))
    window.addEventListener(CART_CHANGED_EVENT, sync)
    // 'storage' only fires in OTHER tabs — the custom event covers this one.
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(CART_CHANGED_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [venueId])
  const persist = useCallback(
    (next: StoreCartLine[]) => {
      setLines(next)
      if (venueId) {
        localStorage.setItem(storageKey(venueId), JSON.stringify(next))
        window.dispatchEvent(new Event(CART_CHANGED_EVENT))
      }
    },
    [venueId],
  )
  const add = useCallback(
    (line: Omit<StoreCartLine, 'quantity'>, quantity = 1) => {
      const existing = lines.find((cartLine) => lineKey(cartLine) === lineKey(line))
      if (existing) {
        persist(lines.map((cartLine) =>
          lineKey(cartLine) === lineKey(line)
            ? {...cartLine, quantity: cartLine.quantity + quantity}
            : cartLine,
        ))
        return
      }
      persist([...lines, {...line, quantity}])
    },
    [lines, persist],
  )
  const setQuantity = useCallback(
    (key: string, quantity: number) => {
      if (quantity < 1) {
        persist(lines.filter((cartLine) => lineKey(cartLine) !== key))
        return
      }
      persist(lines.map((cartLine) => (lineKey(cartLine) === key ? {...cartLine, quantity} : cartLine)))
    },
    [lines, persist],
  )
  const remove = useCallback(
    (key: string) => persist(lines.filter((cartLine) => lineKey(cartLine) !== key)),
    [lines, persist],
  )
  const clear = useCallback(() => persist([]), [persist])
  const count = lines.reduce((acc, line) => acc + line.quantity, 0)
  const total = Math.round(lines.reduce((acc, line) => acc + line.price * line.quantity, 0) * 100) / 100
  return {lines, add, remove, setQuantity, clear, count, total, lineKey}
}
